import type { AIContentClassification, SupportedAIApp } from '../types';

/**
 * Trusted operation → classification map.
 * Classification is ALWAYS taken from this table (or app defaults), never from
 * client-supplied values. Operation IDs match real product flows in the apps.
 */

export type AIOperationDefinition = {
  operation: string;
  app: SupportedAIApp;
  classification: AIContentClassification;
  /** Human label for admin / asset details */
  label: string;
  /** When false, machine-readable provenance is NOT_REQUIRED by default */
  requireMachineReadable?: boolean;
  /** Default visible disclosure prominence */
  visibleDisclosure?: 'subtle' | 'standard' | 'none';
  provider?: string;
  model?: string;
};

const GEMINI_IMAGE = {
  provider: 'google',
  model: 'gemini-3-pro-image-preview',
} as const;

const PHOTOCLEAR_GEMINI = {
  provider: 'google',
  model: 'gemini-3.1-flash-image',
} as const;

/** PhotoClear — quality enhancement without material content change */
const PHOTOCLEAR_ASSISTED: AIOperationDefinition[] = [
  {
    operation: 'photoclear_brighten_enhance',
    app: 'photoclear',
    classification: 'AI_ASSISTED',
    label: 'Brighten & enhance',
    requireMachineReadable: false,
    visibleDisclosure: 'subtle',
    ...PHOTOCLEAR_GEMINI,
  },
  {
    operation: 'photoclear_turn_on_lights',
    app: 'photoclear',
    classification: 'AI_ASSISTED',
    label: 'Turn on lights',
    requireMachineReadable: false,
    visibleDisclosure: 'subtle',
    ...PHOTOCLEAR_GEMINI,
  },
  {
    operation: 'photoclear_window_sky',
    app: 'photoclear',
    classification: 'AI_ASSISTED',
    label: 'Window sky recovery',
    requireMachineReadable: false,
    visibleDisclosure: 'subtle',
    ...PHOTOCLEAR_GEMINI,
  },
  {
    operation: 'photoclear_bed_straighten',
    app: 'photoclear',
    classification: 'AI_ASSISTED',
    label: 'Straighten bed',
    requireMachineReadable: false,
    visibleDisclosure: 'subtle',
    ...PHOTOCLEAR_GEMINI,
  },
  {
    operation: 'photoclear_sofa_straighten',
    app: 'photoclear',
    classification: 'AI_ASSISTED',
    label: 'Straighten sofa',
    requireMachineReadable: false,
    visibleDisclosure: 'subtle',
    ...PHOTOCLEAR_GEMINI,
  },
  {
    operation: 'photoclear_upscale',
    app: 'photoclear',
    classification: 'AI_ASSISTED',
    label: 'Upscale',
    requireMachineReadable: false,
    visibleDisclosure: 'subtle',
    ...PHOTOCLEAR_GEMINI,
  },
];

/** PhotoClear — generative fill / significant object removal / sky / add content */
const PHOTOCLEAR_MODIFIED: AIOperationDefinition[] = [
  {
    operation: 'photoclear_remove_selection',
    app: 'photoclear',
    classification: 'AI_MODIFIED',
    label: 'Remove selection',
    requireMachineReadable: true,
    visibleDisclosure: 'standard',
    ...PHOTOCLEAR_GEMINI,
  },
  {
    operation: 'photoclear_declutter_light',
    app: 'photoclear',
    classification: 'AI_MODIFIED',
    label: 'Light declutter',
    requireMachineReadable: true,
    visibleDisclosure: 'standard',
    ...PHOTOCLEAR_GEMINI,
  },
  {
    operation: 'photoclear_declutter_medium',
    app: 'photoclear',
    classification: 'AI_MODIFIED',
    label: 'Medium declutter',
    requireMachineReadable: true,
    visibleDisclosure: 'standard',
    ...PHOTOCLEAR_GEMINI,
  },
  {
    operation: 'photoclear_declutter_full',
    app: 'photoclear',
    classification: 'AI_MODIFIED',
    label: 'Full declutter',
    requireMachineReadable: true,
    visibleDisclosure: 'standard',
    ...PHOTOCLEAR_GEMINI,
  },
  {
    operation: 'photoclear_garden_declutter',
    app: 'photoclear',
    classification: 'AI_MODIFIED',
    label: 'Tidy garden',
    requireMachineReadable: true,
    visibleDisclosure: 'standard',
    ...PHOTOCLEAR_GEMINI,
  },
  {
    operation: 'photoclear_driveway_declutter',
    app: 'photoclear',
    classification: 'AI_MODIFIED',
    label: 'Tidy drive & bins',
    requireMachineReadable: true,
    visibleDisclosure: 'standard',
    ...PHOTOCLEAR_GEMINI,
  },
  {
    operation: 'photoclear_bins_only',
    app: 'photoclear',
    classification: 'AI_MODIFIED',
    label: 'Remove bins',
    requireMachineReadable: true,
    visibleDisclosure: 'standard',
    ...PHOTOCLEAR_GEMINI,
  },
  {
    operation: 'photoclear_cars_only',
    app: 'photoclear',
    classification: 'AI_MODIFIED',
    label: 'Remove cars',
    requireMachineReadable: true,
    visibleDisclosure: 'standard',
    ...PHOTOCLEAR_GEMINI,
  },
  {
    operation: 'photoclear_blur_personal_photos',
    app: 'photoclear',
    classification: 'AI_MODIFIED',
    label: 'Blur personal photos',
    requireMachineReadable: true,
    visibleDisclosure: 'standard',
    ...PHOTOCLEAR_GEMINI,
  },
  {
    operation: 'photoclear_blur_license_plates',
    app: 'photoclear',
    classification: 'AI_MODIFIED',
    label: 'Blur license plates',
    requireMachineReadable: true,
    visibleDisclosure: 'standard',
    ...PHOTOCLEAR_GEMINI,
  },
  {
    operation: 'photoclear_cut_grass',
    app: 'photoclear',
    classification: 'AI_MODIFIED',
    label: 'Cut the grass',
    requireMachineReadable: true,
    visibleDisclosure: 'standard',
    ...PHOTOCLEAR_GEMINI,
  },
  {
    operation: 'photoclear_remove_shadows',
    app: 'photoclear',
    classification: 'AI_MODIFIED',
    label: 'Remove shadows',
    requireMachineReadable: true,
    visibleDisclosure: 'standard',
    ...PHOTOCLEAR_GEMINI,
  },
  {
    operation: 'photoclear_blue_sky',
    app: 'photoclear',
    classification: 'AI_MODIFIED',
    label: 'Blue sky',
    requireMachineReadable: true,
    visibleDisclosure: 'standard',
    ...PHOTOCLEAR_GEMINI,
  },
  {
    operation: 'photoclear_sunset_sky',
    app: 'photoclear',
    classification: 'AI_MODIFIED',
    label: 'Sunset sky',
    requireMachineReadable: true,
    visibleDisclosure: 'standard',
    ...PHOTOCLEAR_GEMINI,
  },
  {
    operation: 'photoclear_add_flowers',
    app: 'photoclear',
    classification: 'AI_MODIFIED',
    label: 'Add flowers',
    requireMachineReadable: true,
    visibleDisclosure: 'standard',
    ...PHOTOCLEAR_GEMINI,
  },
  {
    operation: 'photoclear_add_plants',
    app: 'photoclear',
    classification: 'AI_MODIFIED',
    label: 'Add plants',
    requireMachineReadable: true,
    visibleDisclosure: 'standard',
    ...PHOTOCLEAR_GEMINI,
  },
  {
    operation: 'photoclear_custom',
    app: 'photoclear',
    classification: 'AI_MODIFIED',
    label: 'Custom edit',
    requireMachineReadable: true,
    visibleDisclosure: 'standard',
    ...PHOTOCLEAR_GEMINI,
  },
  {
    operation: 'photoclear_auto',
    app: 'photoclear',
    classification: 'AI_MODIFIED',
    label: 'Auto declutter',
    requireMachineReadable: true,
    visibleDisclosure: 'standard',
    ...PHOTOCLEAR_GEMINI,
  },
];

const HYPERDUSK_OPS: AIOperationDefinition[] = [
  {
    operation: 'hyperdusk_dusk',
    app: 'hyperdusk',
    classification: 'AI_MODIFIED',
    label: 'HyperDusk still',
    requireMachineReadable: true,
    visibleDisclosure: 'standard',
    provider: 'google',
    model: 'gemini-3-pro-image-preview',
  },
  {
    operation: 'hyperdusk_animation',
    app: 'hyperdusk',
    classification: 'AI_GENERATED',
    label: 'HyperDusk animation',
    requireMachineReadable: true,
    visibleDisclosure: 'standard',
    provider: 'fal',
    model: 'fal-ai/kling-video/o3/pro/image-to-video',
  },
];

const STAGING_OPS: AIOperationDefinition[] = [
  {
    operation: 'virtual_staging',
    app: 'staging',
    classification: 'AI_MODIFIED',
    label: 'Virtual staging',
    requireMachineReadable: true,
    visibleDisclosure: 'standard',
    ...GEMINI_IMAGE,
  },
  {
    operation: 'virtual_staging_clear_furniture',
    app: 'staging',
    classification: 'AI_MODIFIED',
    label: 'Clear furniture',
    requireMachineReadable: true,
    visibleDisclosure: 'standard',
    ...GEMINI_IMAGE,
  },
  {
    operation: 'virtual_staging_match_style',
    app: 'staging',
    classification: 'AI_MODIFIED',
    label: 'Match style',
    requireMachineReadable: true,
    visibleDisclosure: 'standard',
    ...GEMINI_IMAGE,
  },
  {
    operation: 'virtual_staging_same_room',
    app: 'staging',
    classification: 'AI_MODIFIED',
    label: 'Same-room angle',
    requireMachineReadable: true,
    visibleDisclosure: 'standard',
    ...GEMINI_IMAGE,
  },
  {
    operation: 'virtual_staging_360',
    app: 'staging',
    classification: 'AI_MODIFIED',
    label: '360° virtual staging',
    requireMachineReadable: true,
    visibleDisclosure: 'standard',
    ...GEMINI_IMAGE,
  },
  {
    operation: 'virtual_staging_from_scratch',
    app: 'staging',
    classification: 'AI_GENERATED',
    label: 'Generated staging (from scratch)',
    requireMachineReadable: true,
    visibleDisclosure: 'standard',
    ...GEMINI_IMAGE,
  },
];

const IMAGEMOTION_OPS: AIOperationDefinition[] = [
  {
    operation: 'imagemotion_clip',
    app: 'imagemotion',
    classification: 'AI_GENERATED',
    label: 'ImageMotion clip',
    requireMachineReadable: true,
    visibleDisclosure: 'standard',
    provider: 'fal',
  },
  {
    operation: 'imagemotion_compose',
    app: 'imagemotion',
    classification: 'AI_GENERATED',
    label: 'ImageMotion final video',
    requireMachineReadable: true,
    visibleDisclosure: 'standard',
    provider: 'shotstack',
  },
];

export const AI_OPERATIONS: Record<string, AIOperationDefinition> = Object.fromEntries(
  [
    ...PHOTOCLEAR_ASSISTED,
    ...PHOTOCLEAR_MODIFIED,
    ...HYPERDUSK_OPS,
    ...STAGING_OPS,
    ...IMAGEMOTION_OPS,
  ].map((op) => [op.operation, op]),
);

/** App-level defaults when a specific operation is unknown / legacy. */
export const APP_DEFAULT_CLASSIFICATION: Record<SupportedAIApp, AIContentClassification> = {
  photoclear: 'AI_ASSISTED',
  hyperdusk: 'AI_MODIFIED',
  staging: 'AI_MODIFIED',
  imagemotion: 'AI_GENERATED',
};

export const APP_DEFAULT_OPERATION: Record<SupportedAIApp, string> = {
  photoclear: 'photoclear_auto',
  hyperdusk: 'hyperdusk_dusk',
  staging: 'virtual_staging',
  imagemotion: 'imagemotion_clip',
};

export const PROVENANCE_VERSION = '1.0';

export function getOperationDefinition(operation: string): AIOperationDefinition | undefined {
  return AI_OPERATIONS[operation];
}

export function listOperationsForApp(app: SupportedAIApp): AIOperationDefinition[] {
  return Object.values(AI_OPERATIONS).filter((op) => op.app === app);
}
